import { FC, useCallback, useMemo, useState } from 'react';
import { array, number, object } from 'yup';
import { Form, Formik, FormikConfig, useFormikContext } from 'formik';
import { Card } from 'react-bootstrap';

import ELEMENT from 'common/element';
import { validEmailFormat } from 'common/functions';

import Switch from 'components/common/Switch/Switch';
import { MultiSelectInput } from 'components/common/ReactSelect/MultiSelectInput';
import Button, { ButtonVariant } from 'components/common/Button/Button';
import Input from 'components/common/Input/Input';
import { NotificationResponse, NotificationType } from 'components/billingAndUsage/types';

import './NotificationCard.scss';

type NotificationCardProps = {
  title: string;
  description: string;
  notification: NotificationType;
  showDailyThresholdGib: boolean;
  indicateOwnerIsDefault?: boolean;
  onSubmit: (notification: NotificationResponse) => Promise<NotificationResponse | null>;
  testIdContainer?: ELEMENT;
  testIdSwitch?: ELEMENT;
  testIdEmailInput?: ELEMENT;
  testIdSizeInput?: ELEMENT;
  testIdSaveButton?: ELEMENT;
};

type FormValues = {
  emails: string[];
  dailyThresholdGib?: number;
  dailyThresholdPercent?: number;
};

type MultiSelectValidation = {
  isValid: boolean;
  errorMessage: string;
};

const getInitialValue = (notification: NotificationResponse, thresholdRequired: boolean): FormValues => {
  const initialValue: FormValues = { emails: notification.emails };
  if (thresholdRequired) {
    if (notification.dailyThresholdPercent != null) {
      const thresholdPercent = Number(notification.dailyThresholdPercent);
      initialValue.dailyThresholdPercent =
        Number.isNaN(thresholdPercent) || thresholdPercent < 0 ? 0 : thresholdPercent;
    } else {
      const thresholdGib = Number(notification.dailyThresholdGib);
      initialValue.dailyThresholdGib = Number.isNaN(thresholdGib) || thresholdGib < 0 ? 0 : thresholdGib;
    }
  }
  return initialValue;
};

const getValidationSchema = (notification: NotificationResponse, thresholdRequired: boolean) =>
  thresholdRequired
    ? object({
        emails: array(),
        ...(!notification.dailyThresholdPercent && {
          dailyThresholdGib: number().required('Please enter a valid size.').min(0),
        }),
        ...(notification.dailyThresholdPercent && {
          dailyThresholdPercent: number().required('Please enter a valid size.').min(0),
        }),
      })
    : object({
        emails: array(),
      });

const validateEmail =
  (values: string[]) =>
  (email: string): MultiSelectValidation => {
    let errorMessage = '';
    let isValid = true;
    if (email.length > 0) {
      if (values.includes(email)) {
        errorMessage = 'This email is already entered';
        isValid = false;
      }
      if (!validEmailFormat(email)) {
        errorMessage = 'Please enter a valid email';
        isValid = false;
      }
    }
    return {
      isValid,
      errorMessage,
    };
  };

const getEmailError = (errors: string | string[] | undefined): string => {
  if (typeof errors === 'string') {
    return errors;
  }
  if (Array.isArray(errors)) {
    return errors.join('');
  }
  return '';
};

const InnerForm: FC<NotificationCardProps> = (props) => {
  const { indicateOwnerIsDefault = false, testIdEmailInput, testIdSizeInput, testIdSaveButton } = props;

  const { values, errors, setFieldValue, handleChange, isValid, isSubmitting, isValidating, touched, dirty } =
    useFormikContext<FormValues>();

  const [emailInnerInputState, setEmailInnerInputState] = useState({ isEmpty: true, isValid: true });
  const validateEmails = useMemo(() => validateEmail(values.emails), [values.emails]);
  const onEmailInnerInputChange = useCallback(
    (inputValue: string) => {
      const { isValid } = validateEmails(inputValue);
      setEmailInnerInputState({ isValid, isEmpty: inputValue.length === 0 });
    },
    [validateEmails],
  );

  const submitIsDisabled =
    isSubmitting ||
    isValidating ||
    !isValid ||
    !emailInnerInputState.isValid ||
    (!dirty && emailInnerInputState.isEmpty);

  return (
    <Form>
      <MultiSelectInput
        label="Email address(es):"
        placeholder="Enter one or more emails"
        currentValue={values.emails.map((email) => ({ label: email, value: email }))}
        onChangeHandler={(options) => {
          setFieldValue(
            'emails',
            options.map((option) => option.value),
          );
        }}
        validate={validateEmail(values.emails)}
        commonErrorMessage={getEmailError(errors.emails)}
        idContainer={testIdEmailInput}
        onInputChange={onEmailInnerInputChange}
      />
      <div className={'NotificationCard__OwnerIsDefault'}>
        {values.emails.length === 0 && indicateOwnerIsDefault && <p>{'Email of billing plan owner will be used.'}</p>}
      </div>
      <div className="NotificationCard__Footer">
        {typeof values.dailyThresholdPercent !== 'undefined' ? (
          <>
            <Input
              label="Overage: "
              type="number"
              name="dailyThresholdPercent"
              min={'0'}
              value={String(values.dailyThresholdPercent)}
              isInvalid={touched.dailyThresholdPercent && !!errors.dailyThresholdPercent}
              errorMessage={errors.dailyThresholdPercent}
              onChange={handleChange}
              inputClassName="NotificationCard__Size__Input"
              labelClassName="NotificationCard__Size__Field NotificationCard__Size__Percentage__Field"
              labelTextClassName="NotificationCard__Size__Label"
              errorTextClassName="NotificationCard__Size__InvalidText"
              testId={testIdSizeInput}
            />
            <span className="NotificationCard__Size__SecondaryLabel">{'% of total plan'}</span>
          </>
        ) : (
          typeof values.dailyThresholdGib !== 'undefined' && (
            <>
              <div className="NotificationCard__Email">
                <Input
                  label="Size: "
                  type="number"
                  name="dailyThresholdGib"
                  min={'0'}
                  value={String(values.dailyThresholdGib)}
                  isInvalid={touched.dailyThresholdGib && !!errors.dailyThresholdGib}
                  errorMessage={errors.dailyThresholdGib}
                  onChange={handleChange}
                  inputClassName="NotificationCard__Size__Input"
                  labelClassName="NotificationCard__Size__Field"
                  labelTextClassName="NotificationCard__Size__Label"
                  errorTextClassName="NotificationCard__Size__InvalidText"
                  testId={testIdSizeInput}
                />
              </div>
              <span className="NotificationCard__Size__SecondaryLabel">{'GB / day'}</span>
            </>
          )
        )}
        <Button type={'submit'} variant={ButtonVariant.PRIMARY} disabled={submitIsDisabled} testId={testIdSaveButton}>
          Save Changes
        </Button>
      </div>
    </Form>
  );
};

export const NotificationCard: FC<NotificationCardProps> = (props) => {
  const { notification, onSubmit, testIdSwitch, testIdContainer, title, description, showDailyThresholdGib } = props;

  const initialValues = useMemo(
    () => getInitialValue(notification, showDailyThresholdGib),
    [notification, showDailyThresholdGib],
  );
  const validationSchema = useMemo(
    () => getValidationSchema(notification, showDailyThresholdGib),
    [notification, showDailyThresholdGib],
  );

  const onSwitchToggle = useCallback(() => {
    onSubmit({ ...notification, disabled: !notification.disabled });
  }, [onSubmit, notification]);

  const onFormSubmit = useMemo<FormikConfig<FormValues>['onSubmit']>(
    () => async (values, formikHelpers) => {
      const newNotification = await onSubmit({ ...notification, ...values });
      // Here is manual reset required because backend can add additional changes to supplied data.
      // There is a situation when we change ['some@email.com'] (apollo cache) -> [] (form value)
      // and backend transforms [] (form value) back to ['some@email.com'] and put it in apollo cache.
      // It that case changes are not propagated through apollo cache, since it thinks nothing has changed,
      // so value of the form is not reinitialized by a fresh value from apollo cache and stays [].
      // To cover this case, here we manually set it to proper value received from the backend.
      if (newNotification) {
        formikHelpers.resetForm({
          values: getInitialValue(newNotification, showDailyThresholdGib),
        });
      }
    },
    [notification, onSubmit, showDailyThresholdGib],
  );

  return (
    <Formik<FormValues> initialValues={initialValues} validationSchema={validationSchema} onSubmit={onFormSubmit}>
      <Card className="NotificationCard" data-test-id={testIdContainer}>
        <Card.Body>
          <div className="NotificationCard__Title">
            <span className="NotificationCard__Title--Text">{title}</span>
            <Switch checked={!notification.disabled} onChange={onSwitchToggle} testId={testIdSwitch} />
          </div>
          <p className="NotificationCard__Info">{description}</p>
          <InnerForm {...props} />
        </Card.Body>
      </Card>
    </Formik>
  );
};
